import React from 'react';
import { resolveOkiruTemplateAsset } from '../../data/okiruPresentationTemplate';
import './presentation-theme.css';
import './SlideRenderer.css';

// ── Inline text editing ─────────────────────────────────────────────────────
// Presentation Studio renders the real SlideRenderer as its live preview, so
// double-click-to-edit is wired here rather than in a separate mock. Editing
// is off (editable=false) for every other consumer (the learner-facing
// player, the audience screen), which is the default.
const EditContext = React.createContext({ editable: false, editingPath: null, startEdit: () => {}, commit: () => {}, selectedExtraId: null });

function EditableText({ tag = 'div', className, path, value, multiline = true }) {
  const { editable, editingPath, startEdit, commit } = React.useContext(EditContext);
  const ref = React.useRef(null);
  const isEditing = editable && editingPath === path;

  React.useEffect(() => {
    if (!isEditing || !ref.current) return;
    ref.current.focus();
    const range = document.createRange();
    range.selectNodeContents(ref.current);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }, [isEditing]);

  if (!editable) {
    return React.createElement(tag, { className }, value);
  }

  const props = {
    className: `${className || ''} slide-editable${isEditing ? ' slide-editable--active' : ''}`,
    'data-edit-path': path,
  };
  if (isEditing) {
    props.ref = ref;
    props.contentEditable = true;
    props.suppressContentEditableWarning = true;
    props.onPointerDown = (e) => e.stopPropagation();
    props.onBlur = (e) => commit(path, e.currentTarget.textContent);
    props.onKeyDown = (e) => {
      if (e.key === 'Escape') { e.currentTarget.blur(); return; }
      if (!multiline && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    };
  } else {
    props.onDoubleClick = (e) => { e.stopPropagation(); startEdit(path); };
  }
  return React.createElement(tag, props, value);
}

// labelPath/textPath are separate (not derived from a shared base path)
// because some callers pass a fixed, non-editable label alongside real
// slide data for the text (e.g. IntroLayout's "By the end of this module").
function Callout({ label, text, labelPath, textPath }) {
  return (
    <div className="slide-callout">
      <div className="slide-callout-label">
        {labelPath ? <EditableText tag="span" path={labelPath} value={label} multiline={false} /> : label}
      </div>
      <div className="slide-callout-text">
        {textPath ? <EditableText tag="span" path={textPath} value={text} /> : text}
      </div>
    </div>
  );
}

// Renders nothing until a real file exists at the conventional path
// (/presentations/<deck-id>/screenshots/<shot>) — onError hides the broken
// image so the deck degrades gracefully to the text caption alone until the
// screenshot is actually captured and dropped in.
function Screenshot({ src, alt, placeholder }) {
  const [failedSrc, setFailedSrc] = React.useState('');
  if (!src || failedSrc === src) {
    return placeholder ? (
      <div className="slide-screenshot-placeholder">
        <span>Screenshot slot</span>
        <strong>{placeholder}</strong>
      </div>
    ) : null;
  }
  return (
    <img
      className="slide-screenshot"
      src={src}
      alt={alt || ''}
      loading="lazy"
      onError={() => setFailedSrc(src)}
    />
  );
}

// Crops generated concept art to the slide's 16:9 frame. Some generators
// return the requested artwork inside a taller canvas; this keeps incidental
// top/bottom margins out of the presentation without altering the source file.
function ConceptVisual({ src, alt, className = '' }) {
  if (!src) return null;
  return (
    <div className={`concept-visual ${className}`}>
      <img src={src} alt={alt || ''} loading="lazy" />
    </div>
  );
}

function shotSrc(manifest, shot) {
  if (!shot) return null;
  if (shot.startsWith('/')) return shot;
  return manifest ? `/presentations/${manifest.id}/screenshots/${shot}` : null;
}

function Card({ card, index, quoteStyle, manifest }) {
  const base = `cards.${index}`;
  return (
    <div className={`slide-card${quoteStyle ? ' slide-card-quote' : ''}`}>
      <div className="slide-card-title"><EditableText tag="span" path={`${base}.title`} value={card.title} multiline={false} /></div>
      <div className="slide-card-desc">
        {quoteStyle ? (
          <><EditableText tag="span" path={`${base}.description`} value={`"${card.description.replace(/"/g, '')}"`} /></>
        ) : (
          <EditableText tag="span" path={`${base}.description`} value={card.description} />
        )}
      </div>
      <Screenshot src={shotSrc(manifest, card.shot)} alt={card.caption} />
      {card.caption && <div className="slide-caption"><EditableText tag="span" path={`${base}.caption`} value={card.caption} multiline={false} /></div>}
      {card.tag && <div className="slide-card-tag"><EditableText tag="span" path={`${base}.tag`} value={card.tag} multiline={false} /></div>}
    </div>
  );
}

function CardGrid({ cards, columns = 3, quoteStyle = false, manifest }) {
  return (
    <div className="slide-card-grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {cards.map((c, i) => <Card key={i} card={c} index={i} quoteStyle={quoteStyle} manifest={manifest} />)}
    </div>
  );
}

function Checklist({ items }) {
  return (
    <ul className="slide-checklist">
      {items.map((t, i) => (
        <li key={i}><span className="slide-check-mark">✓</span><EditableText tag="span" path={`checks.${i}`} value={t} multiline={false} /></li>
      ))}
    </ul>
  );
}

function colorIsDark(value, fallback = false) {
  const match = String(value || '').trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return fallback;
  const hex = match[1].length === 3 ? match[1].split('').map(c => c + c).join('') : match[1];
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  return (red * 0.299 + green * 0.587 + blue * 0.114) < 150;
}

function Footer({ manifest, index, total, layout }) {
  const theme = manifest?.theme || {};
  const darkLayout = ['title', 'statement', 'intro'].includes(layout);
  const surface = layout === 'title'
    ? theme.heroBackground
    : ['statement', 'intro'].includes(layout)
      ? theme.sectionBackground
      : theme.background;
  const darkBackground = colorIsDark(surface, darkLayout);
  const logo = darkBackground
    ? (manifest?.footerLogoWhite || '/okiru-footer-white.png')
    : (manifest?.footerLogoBlack || '/okiru-footer-black.png');
  const partnerLogo = partnerLogoFor(manifest);
  const clientLogo = clientLogoFor(manifest);
  return (
    <div className="slide-footer">
      <span className="slide-footer-brands" aria-label="Okiru, partner and client branding">
        <img className="slide-footer-okiru" src={logo} alt="Okiru" />
        {partnerLogo && <span className="slide-footer-divider">×</span>}
        {partnerLogo && <img className="slide-footer-claude" src={partnerLogo} alt="Partner logo" />}
        {clientLogo && <span className="slide-footer-divider">|</span>}
        {clientLogo && <img className="slide-footer-client" src={clientLogo} alt={`${manifest?.clientName || 'Client'} logo`} />}
      </span>
      <span className="brand">{manifest?.title || 'Okiru Learn'}</span>
      <span>{String(index + 1).padStart(2, '0')} / {total}</span>
    </div>
  );
}

// A partner logo appears only when a deck sets one. The Claude mark belongs to
// the Claude for Professionals deck alone, so it is ignored anywhere else, even
// if a saved studio copy of another deck still carries it.
const CLAUDE_DECK_ID = 'civils2000-claude-professionals';
// The Claude deck is reused across client companies, so a client logo saved
// into an older copy of it (the Civils2000 logo) is never shown.
function clientLogoFor(manifest) {
  const logo = manifest?.clientLogo || null;
  return logo && /civils2000-logo/i.test(logo) ? null : logo;
}
function partnerLogoFor(manifest) {
  const logo = manifest?.partnerLogo || null;
  if (logo && /claude/i.test(logo) && manifest?.id !== CLAUDE_DECK_ID) return null;
  return logo;
}

// ── Per-layout bodies ────────────────────────────────────────────────────────

function TitleLayout({ s, manifest }) {
  const logo = manifest?.logoDark || '/okiru-logo-white.png';
  const partnerLogo = partnerLogoFor(manifest);
  const clientLogo = clientLogoFor(manifest);
  return (
    <div className="slide-body layout-title">
      <div className="title-brand-lockup" aria-label="Okiru, partner and client branding">
        <img src={logo} alt="Presentation logo" />
        {partnerLogo && <span>×</span>}
        {partnerLogo && <img className="title-partner-logo" src={partnerLogo} alt="Partner logo" />}
        {clientLogo && <span className="title-brand-separator">|</span>}
        {clientLogo && <img className="title-client-logo" src={clientLogo} alt={`${manifest?.clientName || 'Client'} logo`} />}
      </div>
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title slide-title-hero"><EditableText path="title" value={s.title} multiline={false} /></div>
      <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>
      <div className="slide-meta-row">
        {(s.meta || []).map((m, i) => <span key={i} className="slide-meta-pill"><EditableText tag="span" path={`meta.${i}`} value={m} multiline={false} /></span>)}
      </div>
    </div>
  );
}

function StatementLayout({ s }) {
  return (
    <div className="slide-body layout-statement">
      <div className="statement-text"><EditableText path="text" value={s.text} /></div>
      {s.footnote && <div className="slide-lead statement-footnote"><EditableText path="footnote" value={s.footnote} /></div>}
    </div>
  );
}

function AgendaLayout({ s }) {
  return (
    <div className="slide-body layout-agenda">
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      <div className="agenda-columns">
        {(s.columns || []).map((col, ci) => (
          <div className="agenda-col" key={ci}>
            {col.map((row, ri) => (
              <div className="agenda-row" key={ri}>
                <span className="agenda-time"><EditableText tag="span" path={`columns.${ci}.${ri}.time`} value={row.time} multiline={false} /></span>
                <span className="agenda-label"><EditableText tag="span" path={`columns.${ci}.${ri}.label`} value={row.label} multiline={false} /></span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function CardsLayout({ s, manifest }) {
  return (
    <div className={`slide-body layout-cards${s.image ? ' has-concept' : ''}`}>
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      {s.lead && <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>}
      {s.image && <ConceptVisual src={s.image} alt={s.imageAlt} className="cards-concept-visual" />}
      <Screenshot src={shotSrc(manifest, s.shot)} alt={s.caption} />
      {s.caption && <div className="slide-caption" style={{ marginBottom: 10 }}><EditableText path="caption" value={s.caption} multiline={false} /></div>}
      <div className="cards-scroll">
        <CardGrid cards={s.cards} columns={s.columns || 3} quoteStyle={!!s.quoteStyle} manifest={manifest} />
      </div>
      {s.pattern && <Callout label={s.pattern.label} text={s.pattern.text} labelPath="pattern.label" textPath="pattern.text" />}
      {s.footer && <div className="slide-body-text layout-footer-note"><EditableText path="footer" value={s.footer} /></div>}
    </div>
  );
}

function AboutLayout({ s }) {
  return (
    <div className={`slide-body layout-about${s.image ? ' has-visual' : ''}`}>
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>
      <div className="about-stats">
        {(s.stats || []).map((st, i) => (
          <div className="about-stat" key={i}>
            <div className="about-stat-value"><EditableText tag="span" path={`stats.${i}.value`} value={st.value} multiline={false} /></div>
            <div className="about-stat-label"><EditableText tag="span" path={`stats.${i}.label`} value={st.label} multiline={false} /></div>
            <div className="about-stat-sub"><EditableText tag="span" path={`stats.${i}.sub`} value={st.sub} multiline={false} /></div>
          </div>
        ))}
      </div>
      <div className="about-detail-grid">
        <div>
          <div className="about-tags">
            {(s.tags || []).map((t, i) => <span className="slide-meta-pill" key={i}><EditableText tag="span" path={`tags.${i}`} value={t} multiline={false} /></span>)}
          </div>
          <div className="slide-caption"><EditableText path="contact" value={s.contact} multiline={false} /></div>
        </div>
        {s.image && <img className="about-visual" src={s.image} alt={s.imageAlt || ''} />}
      </div>
    </div>
  );
}

function ProfileLayout({ s }) {
  return (
    <div className="slide-body layout-profile">
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="profile-grid">
        {s.photo
          ? <img className="profile-avatar" src={s.photo} alt={s.name} />
          : <div className="profile-avatar" aria-hidden="true">{s.name.split(' ').map(w => w[0]).join('').slice(0, 2)}</div>}
        <div>
          <div className="slide-title" style={{ marginBottom: 2 }}><EditableText path="name" value={s.name} multiline={false} /></div>
          <div className="profile-role"><EditableText path="role" value={s.role} multiline={false} /></div>
          <div className="slide-body-text" style={{ margin: '14px 0' }}><EditableText path="bio" value={s.bio} /></div>
          <ul className="profile-specialisms">
            {(s.specialisms || []).map((sp, i) => <li key={i}><EditableText tag="span" path={`specialisms.${i}`} value={sp} multiline={false} /></li>)}
          </ul>
          <div className="profile-contacts">{s.contacts.join('  ·  ')}</div>
        </div>
      </div>
      {s.footnote && <div className="slide-caption"><EditableText path="footnote" value={s.footnote} multiline={false} /></div>}
    </div>
  );
}

function IntroLayout({ s }) {
  return (
    <div className={`slide-body layout-intro${s.image ? ' has-visual' : ''}`}>
      <div className="intro-module-mark">{String(s.module).padStart(2, '0')}</div>
      <div className="intro-copy">
        <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
        <div className="slide-title slide-title-hero"><EditableText path="title" value={s.title} multiline={false} /></div>
        <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>
        <Callout label="By the end of this module" text={s.outcome} textPath="outcome" />
      </div>
      {s.image && <img className="intro-visual" src={s.image} alt={s.imageAlt || ''} />}
    </div>
  );
}

function LeadLayout({ s }) {
  return (
    <div className={`slide-body layout-lead${s.image ? ' has-visual' : ''}`}>
      <div className="lead-copy">
        <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
        <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
        <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>
        <div className="slide-body-text"><EditableText path="body" value={s.body} /></div>
        {s.callout && <Callout label={s.callout.label} text={s.callout.text} labelPath="callout.label" textPath="callout.text" />}
      </div>
      {s.image && (
        <ConceptVisual
          src={s.image}
          alt={s.imageAlt}
          className={`lead-visual${s.imageChrome === 'none' ? ' concept-visual--bare' : ''}`}
        />
      )}
    </div>
  );
}

function TimelineLayout({ s }) {
  return (
    <div className={`slide-body layout-timeline${s.image ? ' has-concept' : ''}`}>
      {s.image && <img className="timeline-backdrop" src={s.image} alt={s.imageAlt || ''} />}
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>
      <div className="timeline-track">
        {(s.steps || []).map((st, i) => (
          <div className="timeline-step" key={i}>
            <div className="timeline-era"><EditableText tag="span" path={`steps.${i}.era`} value={st.era} multiline={false} /></div>
            <div className="timeline-dot" />
            <div className="timeline-title"><EditableText tag="span" path={`steps.${i}.title`} value={st.title} multiline={false} /></div>
            <div className="timeline-desc"><EditableText tag="span" path={`steps.${i}.description`} value={st.description} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComparisonLayout({ s, manifest }) {
  return (
    <div className="slide-body layout-comparison">
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      {s.lead && <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>}
      <Screenshot src={shotSrc(manifest, s.shot)} alt={s.caption} />
      {s.caption && <div className="slide-caption" style={{ marginBottom: 8 }}><EditableText path="caption" value={s.caption} multiline={false} /></div>}
      <div className="comparison-columns" style={{ gridTemplateColumns: `repeat(${s.columns.length}, 1fr)` }}>
        {(s.columns || []).map((col, ci) => (
          <div className="comparison-col" key={ci}>
            <div className="comparison-heading"><EditableText tag="span" path={`columns.${ci}.heading`} value={col.heading} multiline={false} /></div>
            <ul className="comparison-list">
              {col.items.map((it, ii) => <li key={ii}><EditableText tag="span" path={`columns.${ci}.items.${ii}`} value={it} /></li>)}
            </ul>
          </div>
        ))}
      </div>
      {s.callout && <Callout label={s.callout.label} text={s.callout.text} labelPath="callout.label" textPath="callout.text" />}
      {s.footnote && <div className="slide-caption layout-footer-note"><EditableText path="footnote" value={s.footnote} /></div>}
    </div>
  );
}

function KeytermLayout({ s }) {
  return (
    <div className="slide-body layout-keyterm">
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className={`keyterm-grid${s.image ? ' has-concept' : ''}`}>
        <div>
          <div className="keyterm-term"><EditableText path="term" value={s.term} multiline={false} /></div>
          <div className="slide-lead"><EditableText path="definition" value={s.definition} /></div>
          <div className="slide-body-text"><EditableText path="body" value={s.body} /></div>
        </div>
        <div className="keyterm-callouts">
          {s.image && <ConceptVisual src={s.image} alt={s.imageAlt} className="keyterm-concept-visual" />}
          {(s.callouts || []).map((c, i) => <Callout key={i} label={c.label} text={c.text} labelPath={`callouts.${i}.label`} textPath={`callouts.${i}.text`} />)}
        </div>
      </div>
    </div>
  );
}

function NumberedLayout({ s, manifest }) {
  return (
    <div className={`slide-body layout-numbered${s.shot || s.image ? ' has-visual' : ''}`}>
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      {s.lead && <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>}
      <div className="numbered-content">
        <div className="numbered-list">
          {(s.items || []).map((it, i) => (
            <div className="numbered-item" key={i}>
              <div className="slide-number-badge">{i + 1}</div>
              <div>
                <div className="slide-card-title"><EditableText tag="span" path={`items.${i}.title`} value={it.title} multiline={false} /></div>
                <div className="slide-card-desc"><EditableText tag="span" path={`items.${i}.description`} value={it.description} /></div>
              </div>
            </div>
          ))}
        </div>
        {(s.shot || s.image) && (
          <div className="numbered-visual">
            {s.image
              ? <ConceptVisual src={s.image} alt={s.imageAlt} />
              : <Screenshot src={shotSrc(manifest, s.shot)} alt={s.caption} />}
            {s.caption && <div className="slide-caption"><EditableText path="caption" value={s.caption} multiline={false} /></div>}
          </div>
        )}
      </div>
    </div>
  );
}

function WorkedExampleLayout({ s }) {
  return (
    <div className="slide-body layout-worked-example">
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      <div className="worked-columns">
        <div className="worked-col worked-weak">
          <div className="worked-label"><EditableText tag="span" path="weak.label" value={s.weak.label} multiline={false} /></div>
          <div className="worked-prompt"><EditableText tag="span" path="weak.prompt" value={s.weak.prompt} /></div>
          <div className="worked-result-label">What you get back</div>
          <div className="worked-result"><EditableText tag="span" path="weak.result" value={s.weak.result} /></div>
        </div>
        <div className="worked-col worked-strong">
          <div className="worked-label"><EditableText tag="span" path="strong.label" value={s.strong.label} multiline={false} /></div>
          <div className="worked-prompt"><EditableText tag="span" path="strong.prompt" value={s.strong.prompt} /></div>
          <div className="worked-result-label">What you get back</div>
          <div className="worked-result"><EditableText tag="span" path="strong.result" value={s.strong.result} /></div>
        </div>
      </div>
      {s.footnote && <div className="slide-caption layout-footer-note"><EditableText path="footnote" value={s.footnote} /></div>}
    </div>
  );
}

function ExerciseLayout({ s, manifest }) {
  return (
    <div className="slide-body layout-exercise">
      <div className="exercise-header">
        <div>
          <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
          <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
        </div>
        {s.meta && <span className="slide-meta-pill exercise-meta"><EditableText tag="span" path="meta" value={s.meta} multiline={false} /></span>}
      </div>
      <div className="exercise-steps">
        {(s.steps || []).map((st, i) => (
          <div className="numbered-item" key={i}>
            <div className="slide-number-badge">{i + 1}</div>
            <div>
              <div className="slide-card-title"><EditableText tag="span" path={`steps.${i}.title`} value={st.title} multiline={false} /></div>
              {st.detail && <div className="slide-card-desc"><EditableText tag="span" path={`steps.${i}.detail`} value={st.detail} /></div>}
              <Screenshot src={shotSrc(manifest, st.shot)} alt={st.caption} />
              {st.caption && <div className="slide-caption"><EditableText tag="span" path={`steps.${i}.caption`} value={st.caption} multiline={false} /></div>}
            </div>
          </div>
        ))}
      </div>
      {s.scaffold && (
        <Callout label={s.scaffold.label || 'The task'} text={s.scaffold.text} labelPath="scaffold.label" textPath="scaffold.text" />
      )}
      {s.checkFor && (
        <div className="exercise-check"><strong>You’ll know it worked when…</strong> <EditableText tag="span" path="checkFor" value={s.checkFor} /></div>
      )}
    </div>
  );
}

function OutcomeLayout({ s, manifest }) {
  return (
    <div className="slide-body layout-outcome">
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      <Screenshot src={shotSrc(manifest, s.shot)} alt={s.caption} />
      {s.caption && <div className="slide-caption" style={{ marginBottom: 14 }}><EditableText path="caption" value={s.caption} multiline={false} /></div>}
      <div className="outcome-check-label">Check your work</div>
      <Checklist items={s.checks} />
      {s.footnote && <div className="slide-body-text layout-footer-note"><EditableText path="footnote" value={s.footnote} /></div>}
    </div>
  );
}

function PatternLayout({ s, manifest }) {
  return (
    <div className="slide-body layout-pattern">
      <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
      <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
      {s.lead && <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>}
      <div className="pattern-fields">
        {(s.fields || []).map((f, i) => (
          <div className="pattern-field" key={i}>
            <div className="pattern-field-label"><EditableText tag="span" path={`fields.${i}.label`} value={f.label} multiline={false} /></div>
            <div className="pattern-field-value"><EditableText tag="span" path={`fields.${i}.value`} value={f.value} /></div>
          </div>
        ))}
      </div>
      <Screenshot src={shotSrc(manifest, s.shot)} alt={s.caption} />
      {s.caption && <div className="slide-caption"><EditableText path="caption" value={s.caption} multiline={false} /></div>}
      {s.footnote && <div className="slide-body-text layout-footer-note"><EditableText path="footnote" value={s.footnote} /></div>}
    </div>
  );
}

// Self-contained, take-it-and-run practice prompt: scenario → task → a
// ready-to-paste prompt → what a good result looks like → a small variation
// to try alone. Distinct from the `exercise` layout (a live, facilitated,
// in-room activity) — this is supplementary practice a learner can work
// through solo, before or after the day, so it always carries its own badge.
function ExerciseExampleLayout({ s }) {
  return (
    <div className="slide-body layout-exercise-example">
      <div className="exercise-example-badge">Exercise Example</div>
      <div className="exercise-header">
        <div>
          <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
          <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
        </div>
        {s.meta && <span className="slide-meta-pill exercise-meta"><EditableText tag="span" path="meta" value={s.meta} multiline={false} /></span>}
      </div>
      <div className="exercise-example-row">
        <div className="exercise-example-field">
          <div className="exercise-example-field-label">Scenario</div>
          <div className="slide-card-desc"><EditableText tag="span" path="scenario" value={s.scenario} /></div>
        </div>
        <div className="exercise-example-field">
          <div className="exercise-example-field-label">Task</div>
          <div className="slide-card-desc"><EditableText tag="span" path="task" value={s.task} /></div>
        </div>
      </div>
      <div className="exercise-example-prompt">
        <div className="exercise-example-field-label">Example Prompt</div>
        <div className="exercise-example-prompt-text"><EditableText path="examplePrompt" value={s.examplePrompt} /></div>
      </div>
      <div className="exercise-example-row">
        <div className="exercise-example-field">
          <div className="exercise-example-field-label">Expected Result</div>
          <div className="slide-card-desc"><EditableText tag="span" path="expectedResult" value={s.expectedResult} /></div>
        </div>
        <div className="exercise-example-field">
          <div className="exercise-example-field-label">Try It Yourself</div>
          <div className="slide-card-desc"><EditableText tag="span" path="tryItYourself" value={s.tryItYourself} /></div>
        </div>
      </div>
    </div>
  );
}

function MediaLayout({ s }) {
  const isYouTube = s.mediaType === 'youtube' && s.videoId;
  const isVideo = s.mediaType === 'video' || /\.(mp4|webm)(\?|$)/i.test(s.image || '');
  return (
    <div className="slide-body layout-media">
      <div className="media-copy">
        {s.kicker && <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>}
        {s.title && <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>}
        {s.lead && <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>}
      </div>
      {isYouTube
        ? <a
            className="media-hero media-hero--yt"
            href={s.link || `https://www.youtube.com/watch?v=${s.videoId}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Watch ${s.title || 'this video'} on YouTube`}
          >
            <img
              className="media-yt-thumb"
              src={`https://img.youtube.com/vi/${s.videoId}/maxresdefault.jpg`}
              onError={(e) => {
                // Fall back through YouTube's 16:9 thumbnail sizes only —
                // hqdefault is 4:3 with baked-in letterbox bars and crops badly.
                const img = e.currentTarget;
                const fallbacks = ['hq720', 'mqdefault'];
                const step = Number(img.dataset.fallback || 0);
                if (step < fallbacks.length) {
                  img.dataset.fallback = String(step + 1);
                  img.src = `https://img.youtube.com/vi/${s.videoId}/${fallbacks[step]}.jpg`;
                }
              }}
              alt={s.imageAlt || s.title || ''}
              crossOrigin="anonymous"
            />
            <span className="media-yt-play" aria-hidden="true" />
          </a>
        : isVideo
        ? <video className="media-hero" src={s.image} controls autoPlay={!!s.autoplay} muted={!!s.autoplay} loop={!!s.loop} />
        : <img className="media-hero" src={s.image} alt={s.imageAlt || s.title || ''} />}
      {s.link && (
        <a className="media-link" href={s.link} target="_blank" rel="noopener noreferrer">
          <span className="media-link-icon" aria-hidden="true">▶</span>
          {s.linkLabel || s.link.replace(/^https?:\/\/(www\.)?/, '')}
        </a>
      )}
      {s.caption && <div className="slide-caption"><EditableText path="caption" value={s.caption} multiline={false} /></div>}
    </div>
  );
}

function WalkthroughLayout({ s, manifest }) {
  return (
    <div className="slide-body layout-walkthrough">
      <div className="walkthrough-copy">
        <div className="slide-kicker"><EditableText path="kicker" value={s.kicker} multiline={false} /></div>
        <div className="walkthrough-progress">
          <span>Step {s.step || 1} of {s.totalSteps || 1}</span>
          <div><i style={{ width: `${((s.step || 1) / (s.totalSteps || 1)) * 100}%` }} /></div>
        </div>
        <div className="slide-title"><EditableText path="title" value={s.title} multiline={false} /></div>
        {s.lead && <div className="slide-lead"><EditableText path="lead" value={s.lead} /></div>}
        {s.instruction && <div className="slide-callout"><div className="slide-callout-label">Do this</div><div className="slide-callout-text"><EditableText tag="span" path="instruction" value={s.instruction} /></div></div>}
        {s.tip && <div className="slide-body-text walkthrough-tip"><EditableText path="tip" value={s.tip} /></div>}
      </div>
      <div className="walkthrough-screen">
        <Screenshot
          src={shotSrc(manifest, s.shot)}
          alt={s.caption}
          placeholder={s.screenshotPlaceholder || s.caption || 'Add the matching Claude screen'}
        />
        {s.caption && <div className="slide-caption"><EditableText path="caption" value={s.caption} multiline={false} /></div>}
      </div>
    </div>
  );
}

// Renders a slide imported from PowerPoint (see pptxImport.js): every
// element carries its own absolute position (as a percentage of the
// original slide's width/height) instead of flowing through one of the
// semantic Okiru layouts above.
function FreeformLayout({ s }) {
  const background = s.background;
  const box = (el) => ({ left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, height: `${el.h}%` });
  return (
    <div className="slide-body layout-freeform">
      {background && (
        <div
          className="freeform-bg"
          style={
            background.type === 'image'
              ? { backgroundImage: `url(${background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : { background: background.value }
          }
        />
      )}
      <div className="freeform-canvas">
        {(s.elements || []).map((el, i) => {
          if (el.type === 'image')
            return <img key={i} className="freeform-el freeform-image" style={box(el)} src={el.src} alt={el.alt || ''} />;
          if (el.type === 'table')
            return (
              <table key={i} className="freeform-el freeform-table" style={box(el)}>
                <tbody>
                  {(el.rows || []).map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, c) => (
                        <td key={c}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          return (
            <div
              key={i}
              className="freeform-el freeform-text"
              style={{
                ...box(el),
                background: el.gradient || el.fill || undefined,
                border: el.border ? `${el.border.width}px solid ${el.border.color}` : undefined,
                borderRadius: el.shape === 'ellipse' ? '50%' : el.shape === 'round' ? '10%' : undefined,
              }}
            >
              {(el.paragraphs || []).map((p, pi) => (
                <p
                  key={pi}
                  className={p.hasBullet ? 'freeform-bullet' : ''}
                  style={{ textAlign: p.align, paddingLeft: p.level ? `${p.level * 18}px` : undefined }}
                >
                  {p.runs.map((run, ri) => (
                    <span
                      key={ri}
                      style={{
                        fontWeight: run.bold ? 700 : undefined,
                        fontStyle: run.italic ? 'italic' : undefined,
                        textDecoration: run.underline ? 'underline' : undefined,
                        color: run.color,
                        fontSize: run.fontSize ? `${run.fontSize}px` : undefined,
                        fontFamily: run.fontFamily,
                      }}
                    >
                      {run.text}
                    </span>
                  ))}
                </p>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const LAYOUTS = {
  title: TitleLayout,
  freeform: FreeformLayout,
  statement: StatementLayout,
  agenda: AgendaLayout,
  cards: CardsLayout,
  about: AboutLayout,
  profile: ProfileLayout,
  intro: IntroLayout,
  lead: LeadLayout,
  timeline: TimelineLayout,
  comparison: ComparisonLayout,
  keyterm: KeytermLayout,
  numbered: NumberedLayout,
  'worked-example': WorkedExampleLayout,
  exercise: ExerciseLayout,
  outcome: OutcomeLayout,
  pattern: PatternLayout,
  'exercise-example': ExerciseExampleLayout,
  media: MediaLayout,
  walkthrough: WalkthroughLayout,
};

// ── Formatting and inserted elements (edited in the Presentation Studio) ─────
// slide.styles[element] = { fontSize, color, bold, italic, underline, align }
// for the layout's own title, lead, body and kicker text. slide.extras holds
// free elements placed on the 1280 x 720 canvas: text boxes, images, shapes
// and tables, each { id, type, x, y, w, h, ... }.
export const STYLE_ELEMENTS = ['title', 'lead', 'body', 'kicker'];

function styleAttributes(styles) {
  const attrs = {};
  const vars = {};
  for (const el of STYLE_ELEMENTS) {
    const st = styles?.[el];
    if (!st) continue;
    if (st.fontSize) { attrs[`data-st-${el}-fs`] = ''; vars[`--st-${el}-fs`] = `${st.fontSize}px`; }
    if (st.color) { attrs[`data-st-${el}-color`] = ''; vars[`--st-${el}-color`] = st.color; }
    if (st.bold !== undefined) attrs[`data-st-${el}-bold`] = st.bold ? '1' : '0';
    if (st.italic) attrs[`data-st-${el}-italic`] = '1';
    if (st.underline) attrs[`data-st-${el}-underline`] = '1';
    if (st.align) { attrs[`data-st-${el}-align`] = ''; vars[`--st-${el}-align`] = st.align; }
  }
  return { attrs, vars };
}

export function extraTextStyle(st = {}) {
  return {
    fontSize: st.fontSize ? `${st.fontSize}px` : undefined,
    color: st.color || undefined,
    fontWeight: st.bold ? 800 : undefined,
    fontStyle: st.italic ? 'italic' : undefined,
    textDecoration: st.underline ? 'underline' : undefined,
    textAlign: st.align || undefined,
  };
}

function ExtraElement({ extra, index }) {
  const { editable, selectedExtraId } = React.useContext(EditContext);
  const selected = editable && selectedExtraId === extra.id;
  const base = `extras.${index}`;
  const box = { left: extra.x, top: extra.y, width: extra.w, height: extra.h };
  const text = extraTextStyle(extra.style);
  let body = null;
  if (extra.type === 'image') {
    body = extra.url ? <img src={extra.url} alt={extra.alt || ''} className="slide-extra-img" draggable={false} /> : <div className="slide-extra-empty">Image</div>;
  } else if (extra.type === 'shape') {
    body = <div className="slide-extra-shape" style={{ background: extra.fill || 'var(--slide-accent)', borderRadius: extra.shape === 'ellipse' ? '50%' : `${extra.radius ?? 12}px` }} />;
  } else if (extra.type === 'table') {
    const rows = extra.cells || [];
    body = (
      <table className="slide-extra-table" style={text}>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className={r === 0 && extra.header !== false ? 'slide-extra-table-head' : ''}>
              {row.map((cell, c) => (
                <td key={c}><EditableText tag="span" path={`${base}.cells.${r}.${c}`} value={cell} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  } else {
    body = <EditableText className="slide-extra-text" path={`${base}.text`} value={extra.text || ''} />;
  }
  return (
    <div className={`slide-extra slide-extra--${extra.type}${editable ? ' slide-extra--editable' : ''}${selected ? ' is-selected' : ''}`} data-extra-id={extra.id} style={{ ...box, background: extra.type === 'text' && extra.fill ? extra.fill : undefined, ...(extra.type === 'text' ? text : {}) }}>
      {body}
      {selected && <span className="slide-extra-handle" data-resize-handle="" aria-hidden="true" />}
    </div>
  );
}

export default function SlideRenderer({ slide, index, total, manifest, editable, editingPath, onStartEdit, onCommitEdit, selectedExtraId }) {
  const Layout = LAYOUTS[slide.layout];
  const theme = manifest?.theme || {};
  const darkLayout = ['title', 'statement', 'intro'].includes(slide.layout);
  const templateTone = darkLayout || manifest?.templateId === 'okiru-dark' ? 'dark' : 'light';
  const templateAsset = slide.templateAsset
    ? resolveOkiruTemplateAsset(slide.templateAsset, templateTone)
    : null;
  const renderedSlide = templateAsset
    ? { ...slide, image: templateAsset.src, imageAlt: slide.imageAlt || templateAsset.alt, imageChrome: slide.imageChrome || 'none' }
    : slide;
  const themeStyle = {
    '--slide-bg': theme.background || undefined,
    '--slide-bg-alt': theme.backgroundAlt || undefined,
    '--slide-bg-card': theme.card || undefined,
    '--slide-ink': theme.text || undefined,
    '--slide-ink-soft': theme.textSoft || undefined,
    '--slide-ink-faint': theme.textFaint || undefined,
    '--slide-line': theme.line || undefined,
    '--slide-accent': theme.accent || undefined,
    '--slide-accent-deep': theme.accentDeep || undefined,
    '--slide-accent-soft': theme.accentSoft || undefined,
    '--slide-brand-cyan': theme.brandCyan || undefined,
    '--slide-brand-blue': theme.brandBlue || undefined,
    '--slide-brand-magenta': theme.brandMagenta || undefined,
    '--slide-brand-orange': theme.brandOrange || undefined,
    '--slide-good': theme.good || undefined,
    '--slide-good-soft': theme.goodSoft || undefined,
    '--slide-font-display': theme.displayFont || undefined,
    '--slide-font-body': theme.bodyFont || undefined,
    '--slide-hero-background': theme.heroBackground || undefined,
    '--slide-section-background': theme.sectionBackground || undefined,
    '--slide-title-x': `${slide.positions?.title?.x || 0}px`,
    '--slide-title-y': `${slide.positions?.title?.y || 0}px`,
    '--slide-lead-x': `${slide.positions?.lead?.x || 0}px`,
    '--slide-lead-y': `${slide.positions?.lead?.y || 0}px`,
    '--slide-body-x': `${slide.positions?.body?.x || 0}px`,
    '--slide-body-y': `${slide.positions?.body?.y || 0}px`,
    '--slide-kicker-x': `${slide.positions?.kicker?.x || 0}px`,
    '--slide-kicker-y': `${slide.positions?.kicker?.y || 0}px`,
    '--slide-image-x': `${slide.positions?.image?.x || 0}px`,
    '--slide-image-y': `${slide.positions?.image?.y || 0}px`,
    '--slide-image-focus': slide.imageFocus || 'center',
    '--slide-image-zoom': slide.imageZoom || 1,
  };
  const formatting = styleAttributes(slide.styles);
  Object.assign(themeStyle, formatting.vars);
  return (
    <EditContext.Provider value={{ editable: !!editable, editingPath: editingPath || null, startEdit: onStartEdit || (() => {}), commit: onCommitEdit || (() => {}), selectedExtraId: selectedExtraId || null }}>
      <div className="slide-canvas slide-canvas-transition" data-layout={slide.layout} data-tone={darkLayout ? 'dark' : 'light'} style={themeStyle} {...formatting.attrs}>
        <div key={`${slide.id}-${index}`} className={`slide-animation slide-animation--${slide.animation || (darkLayout ? 'fade' : 'rise')}`}>
          {Layout ? <Layout s={renderedSlide} manifest={manifest} /> : <div className="slide-body">Unknown layout: {slide.layout}</div>}
        </div>
        {Array.isArray(slide.extras) && slide.extras.length > 0 && (
          <div className="slide-extras">
            {slide.extras.map((extra, i) => <ExtraElement key={extra.id || i} extra={extra} index={i} />)}
          </div>
        )}
        <Footer manifest={manifest} index={index} total={total} layout={slide.layout} />
      </div>
    </EditContext.Provider>
  );
}
